import React, { useState } from 'react';
import type { RenderDiff } from '../types';
import { computeDiffClient } from '../lib/render-diff-client';

interface RenderDiffPanelProps {
  diff: RenderDiff;
  /** CSR HTML cached from the URL scrape (for manual SSR re-diff) */
  csrHtml: string | null;
  onManualSsrDiff?: (newDiff: RenderDiff) => void;
}

const META_LABELS: Record<keyof RenderDiff['metaAddedByJs'], string> = {
  viewport: 'Viewport',
  canonical: 'Canonical',
  description: 'Meta description',
  openGraph: 'Open Graph',
  twitterCard: 'Twitter Card',
};

export const RenderDiffPanel: React.FC<RenderDiffPanelProps> = ({ diff: d, csrHtml, onManualSsrDiff }) => {
  if (!d.ssrAvailable) {
    return <UnavailablePanel csrHtml={csrHtml} onManualSsrDiff={onManualSsrDiff} />;
  }

  const ratio = d.csrSize > 0 ? d.ssrSize / d.csrSize : 0;
  const ratioPct = Math.round(ratio * 100);
  const headingsDelta = d.headingsCountCsr - d.headingsCountSsr;

  const metaItems = (Object.keys(META_LABELS) as Array<keyof typeof META_LABELS>)
    .map((k) => ({ key: k, label: META_LABELS[k], jsOnly: d.metaAddedByJs[k] }));
  const jsOnlyMetas = metaItems.filter((m) => m.jsOnly);

  const hasIssues =
    d.schemaTypesAddedByJs.length > 0 ||
    d.schemaTypesRemovedByJs.length > 0 ||
    d.h1AddedByJs ||
    d.titleAddedByJs ||
    jsOnlyMetas.length > 0 ||
    headingsDelta > 5;

  return (
    <div className="card">
      <span className="eyebrow">Pre-render vs post-render</span>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, margin: '8px 0 16px' }}>
        What only JavaScript adds
      </h3>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--rule)' }}>
        <Metric label="Server HTML" value={`${(d.ssrSize / 1000).toFixed(0)}k chars`} />
        <Metric label="Rendered HTML" value={`${(d.csrSize / 1000).toFixed(0)}k chars`} />
        <Metric label="SSR coverage" value={`${ratioPct}%`} />
        <Metric label="Headings" value={`${d.headingsCountSsr} → ${d.headingsCountCsr}`} />
      </div>

      {!hasIssues ? (
        <p style={{ margin: 0, color: 'var(--score-high)', fontSize: 14 }}>
          ✓ Server-rendered output already includes all the SEO-critical content. Non-JS crawlers see the same outline, schema, and meta as users.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {d.schemaTypesAddedByJs.length > 0 && (
            <DiffRow tone="critical" label="Schema added by JS only"
              detail={d.schemaTypesAddedByJs.join(', ')}
              note="Non-rendering crawlers (Bing, Slackbot, Twitterbot) won't see these. Move JSON-LD to server-rendered HTML." />
          )}
          {d.schemaTypesRemovedByJs.length > 0 && (
            <DiffRow tone="warning" label="Schema removed by JS"
              detail={d.schemaTypesRemovedByJs.join(', ')}
              note="JS is stripping schema present in the server response — almost always a bug." />
          )}
          {d.h1AddedByJs && (
            <DiffRow tone="critical" label="H1 only appears after JS"
              detail="The page has no <h1> in the server response."
              note="Non-rendering crawlers see no topic signal. Render the H1 server-side." />
          )}
          {d.titleAddedByJs && (
            <DiffRow tone="critical" label="<title> only set by JS"
              detail="Server response has no title."
              note="Render the title server-side; many crawlers ignore JS-set titles." />
          )}
          {jsOnlyMetas.map((m) => (
            <DiffRow key={m.key}
              tone={m.key === 'canonical' ? 'critical' : m.key === 'twitterCard' ? 'info' : 'warning'}
              label={`${m.label} only set by JS`}
              detail="Missing from the server response."
              note={
                m.key === 'canonical'
                  ? 'Without a canonical URL pre-render, duplicate-content variants can compete in the index.'
                  : m.key === 'description' || m.key === 'openGraph'
                  ? 'Link previews on Slack, iMessage, Discord, and AI assistants will fall back to scraped content or be empty.'
                  : 'Mobile-friendliness signal missing pre-render.'
              } />
          ))}
          {headingsDelta > 5 && (
            <DiffRow tone="warning"
              label={`${headingsDelta} headings only appear after JS`}
              detail={`SSR: ${d.headingsCountSsr} · CSR: ${d.headingsCountCsr}`}
              note="Most of the document outline is JS-generated. Check that key navigational/topical headings are in the server response." />
          )}
        </ul>
      )}
    </div>
  );
};

const UnavailablePanel: React.FC<{ csrHtml: string | null; onManualSsrDiff?: (d: RenderDiff) => void }> = ({ csrHtml, onManualSsrDiff }) => {
  const [open, setOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canCompute = !!csrHtml && !!onManualSsrDiff;

  const compute = () => {
    if (!csrHtml || !onManualSsrDiff) return;
    const trimmed = pasted.trim();
    if (!trimmed) {
      setError('Paste an HTML snapshot first.');
      return;
    }
    try {
      const newDiff = computeDiffClient(trimmed, csrHtml);
      onManualSsrDiff(newDiff);
      setError(null);
      setOpen(false);
      setPasted('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to parse the SSR HTML.');
    }
  };

  return (
    <div className="card">
      <span className="eyebrow">Pre-render vs post-render</span>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 22, margin: '8px 0 12px' }}>
        SSR view unavailable
      </h3>
      <p style={{ margin: '0 0 12px', color: 'var(--ink-2)', fontSize: 14.5, lineHeight: 1.55 }}>
        Cloudflare or similar bot protection blocked our non-JS fetch.{' '}
        <strong>This doesn't necessarily mean Googlebot is blocked</strong> — real Google crawlers
        are whitelisted via reverse DNS, while ours aren't. To verify your site directly, try this
        from a terminal:
      </p>
      <pre style={{
        background: 'var(--graphite)',
        color: 'var(--paper)',
        padding: '10px 14px',
        borderRadius: 4,
        fontFamily: 'var(--font-mono)',
        fontSize: 12.5,
        margin: '0 0 16px',
        overflowX: 'auto',
      }}>
{`curl -sA 'Googlebot/2.1 (+http://www.google.com/bot.html)' <your-url> | head -c 2000`}
      </pre>

      {canCompute && !open && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setOpen(true)}
        >
          Paste your own SSR view
        </button>
      )}

      {canCompute && open && (
        <div style={{ marginTop: 16 }}>
          <label className="eyebrow" htmlFor="manual-ssr" style={{ display: 'block', marginBottom: 8 }}>
            Paste pre-JS HTML
          </label>
          <p className="meta" style={{ margin: '0 0 10px', color: 'var(--ink-3)' }}>
            Run the curl above (or use <code>view-source:</code> in your browser) and paste the result. We'll recompute the diff entirely in your browser — no server call.
          </p>
          <textarea
            id="manual-ssr"
            className="textarea"
            placeholder="<!-- Paste non-JS HTML response here -->"
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            spellCheck={false}
            style={{ minHeight: 160 }}
          />
          {error && (
            <div role="alert" style={{ marginTop: 8, fontSize: 13, color: 'var(--score-low)' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'transparent', color: 'var(--ink-3)', padding: '8px 14px' }}
              onClick={() => { setOpen(false); setPasted(''); setError(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={compute}
              disabled={!pasted.trim()}
            >
              Compute diff
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="meta" style={{ marginBottom: 2 }}>{label}</div>
    <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.2 }}>
      {value}
    </div>
  </div>
);

const DiffRow: React.FC<{ tone: 'critical' | 'warning' | 'info'; label: string; detail: string; note: string }> = ({ tone, label, detail, note }) => {
  const cls = tone === 'critical' ? 'pill-critical' : tone === 'warning' ? 'pill-warning' : 'pill-info';
  const toneLabel = tone === 'critical' ? 'Critical' : tone === 'warning' ? 'Warning' : 'Info';
  return (
    <li style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 0', borderTop: '1px dashed var(--rule)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className={`pill ${cls}`}>{toneLabel}</span>
        <strong style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{label}</strong>
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink-2)' }}>{detail}</div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{note}</div>
    </li>
  );
};
